import { settingsSchema, type SettingsRecord } from "../../../contracts/storage/schemas.ts";
import type { CredentialRecord, CredentialStore, SettingsStore } from "../../../application/ports/settings.ts";

const CREDENTIAL_KEY = "vocably.credentials.v1";
const SETTINGS_KEY = "vocably.settings.v1";

export class LocalCredentialStore implements CredentialStore {
  constructor(private readonly storage: Storage = window.localStorage) {}

  async get(): Promise<CredentialRecord | null> {
    try {
      const raw = this.storage.getItem(CREDENTIAL_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as CredentialRecord;
      if (parsed.provider === "gemini" && typeof parsed.apiKey === "string") {
        return parsed;
      }
      return null;
    } catch {
      return null;
    }
  }

  async save(record: CredentialRecord): Promise<void> {
    this.storage.setItem(CREDENTIAL_KEY, JSON.stringify(record));
  }

  async clear(): Promise<void> {
    this.storage.removeItem(CREDENTIAL_KEY);
  }
}

export class LocalSettingsStore implements SettingsStore {
  private readonly listeners = new Set<(record: SettingsRecord) => void>();

  constructor(private readonly storage: Storage = window.localStorage) {}

  async get(): Promise<SettingsRecord> {
    try {
      const raw = this.storage.getItem(SETTINGS_KEY);
      if (!raw) {
        return { aiMode: "gemini" };
      }
      return settingsSchema.parse(JSON.parse(raw));
    } catch {
      return { aiMode: "gemini" };
    }
  }

  async save(record: SettingsRecord): Promise<void> {
    const parsed = settingsSchema.parse(record);
    this.storage.setItem(SETTINGS_KEY, JSON.stringify(parsed));
    for (const listener of this.listeners) {
      listener(parsed);
    }
  }

  subscribe(listener: (record: SettingsRecord) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

export { CREDENTIAL_KEY, SETTINGS_KEY };
